// @ts-strict-ignore
/* eslint-disable no-console */

const debug =
  typeof window === 'undefined'
    ? (process.env.RAZZLE_NODE_ENV || process.env.NODE_ENV) === 'development'
    : window.location.hostname === 'localhost'

// Can throw if given recursive object
// based on: https://stackoverflow.com/a/7616484
export const hash = (maybeStr: any) => {
  let str = maybeStr
  if (typeof maybeStr !== 'string') {
    str = JSON.stringify(maybeStr)
  }
  let hashRes = 0
  if (str === undefined || str.length === 0 || str === '"undefined"') return hashRes
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hashRes = (hashRes << 5) - hashRes + char
    hashRes |= 0 // Convert to 32bit integer
  }
  return hashRes
}

// based on: https://github.com/bttmly/is-pojo/blob/master/lib/index.js
export const isPojo = (obj: any) => {
  if (obj === null || typeof obj !== 'object') {
    return false
  }
  return Object.getPrototypeOf(obj) === Object.prototype && !isReactElement(obj)
}

export const isPojoDeep = (obj: any, parent = '') => {
  if (!isPojo(obj)) {
    return false
  } else {
    for (const key of Object.keys(obj)) {
      if (Array.isArray(obj[key])) {
        const path = `${parent ? `${parent}.` : ''}${key}`
        const index = 0
        for (const item of obj[key]) {
          if (!isPrimitive(item) && !isPojoDeep(item, `${path}[${index}]`)) {
            return false
          }
        }
      } else {
        const path = `${parent ? `${parent}.` : ''}${key}`
        if (!isPrimitive(obj[key]) && !isPojoDeep(obj[key], path)) {
          return false
        }
      }
    }
    return true
  }
}

// Primitive values: string, number, boolean, null, undefined
export const isPrimitive = (x: any) => {
  const type = typeof x
  return ['string', 'number', 'undefined', 'boolean'].includes(type) || x === null
}

// This is not 100% but accurate enough for this use case
export const isReactElement = (obj: any) => {
  return (
    typeof obj === 'object' && obj !== null && Object.prototype.hasOwnProperty.call(obj, '$$typeof')
  )
}

// Is the given argument one of:
// "primitive", "plain object", "React element", "function", "function", "array (...)" or "non-plain object"
// @ts-ignore
export const valueCategory = (x: any): string => {
  if (isPrimitive(x)) return 'primitive'
  if (isPojoDeep(x)) return 'plain object'
  if (isReactElement(x)) return 'React element'
  if (typeof x === 'function') return 'function'
  if (Array.isArray(x)) {
    if (x.length === 0) {
      return 'array (empty)'
    } else {
      const t = valueCategory(x[0])
      let allTheSame = true
      for (const el of x) {
        const thisCategory = valueCategory(el)
        if (thisCategory !== t) {
          allTheSame = false
          break
        }
      }
      return allTheSame ? `array (${t})` : 'array (mixed)'
    }
  }
  if (typeof x === 'object') return 'non-plain object'
}

// Are two primitive values equal for re-rendering purposes
// Note: if both values are NaN, no need to re-render so they are considered equal
export const equalPrimitives = (a: any, b: any) => {
  if (typeof a === 'number') {
    if (typeof b === 'number') {
      return isNaN(a) ? isNaN(b) : a === b
    } else {
      return false
    }
  } else {
    return typeof a === typeof b && a === b
  }
}

// Returns true if both args are either primitives or POJOs and have the same value
export const equalPrimitivesOrPojo = (a: any, b: any) => {
  if (a === null) {
    return b === null
  }
  if (isPojoDeep(a)) {
    if (isPojoDeep(b)) {
      try {
        return hash(a) === hash(b)
      } catch (ex) {
        logError('Hashing failed', ex)
        return false
      }
    } else {
      return false
    }
  } else if (isPrimitive(a)) {
    return equalPrimitives(a, b)
  } else {
    logError(a, b)
    throw new Error('Both values not primitives or POJOs')
  }
}

// Takes two objects as args and compares their properties.
// - properties that are non-pojo objects (functions, React elements) are not compared
// - compare primitive props (null, undefined, string, number, boolean) by value
// - compare plain objects (POJOs) props by hash
// - for array props of the same length, items are compared using this same function recursively
// - if null compared with non-null, throws
// - if any new props present on second object, throws
export const getChangedOrMissingPropertiesHashed = (oldest: any, newest: any, debug = false) => {
  if (oldest === null || newest === null) {
    if (oldest === null && newest === null) {
      return []
    } else {
      throw new Error('A null value does not have properties')
    }
  }
  if (typeof oldest !== 'object' || typeof newest !== 'object') {
    throw new Error('Can only compare objects')
  }

  if (oldest === newest) {
    return []
  }

  const oldKeys = Object.keys(oldest)
  const newKeys = Object.keys(newest)
  const changedOrMissing = []

  for (const key of oldKeys) {
    if (!newKeys.includes(key)) {
      changedOrMissing.push(key)
    } else {
      if (isPojoDeep(oldest[key])) {
        if (!isPojoDeep(newest[key])) {
          changedOrMissing.push(key)
        } else {
          let oldHash, newHash
          try {
            oldHash = hash(oldest[key])
          } catch (ex) {
            logError(`Could not hash old prop "${key}"`, oldest[key])
            logError(ex)
          }
          try {
            newHash = hash(newest[key])
          } catch (ex) {
            logError(`Could not hash new prop "${key}"`, newest[key])
            logError(ex)
          }
          if (newHash !== oldHash) {
            if (debug) {
              console.log(
                'changed',
                JSON.parse(JSON.stringify(oldest[key])),
                JSON.parse(JSON.stringify(newest[key])),
              )
            }
            changedOrMissing.push(key)
          }
        }
      } else if (isPrimitive(oldest[key])) {
        if (!isPrimitive(newest[key])) {
          changedOrMissing.push(key)
        } else {
          if (!equalPrimitives(newest[key], oldest[key])) {
            changedOrMissing.push(key)
          }
        }
      } else if (Array.isArray(oldest[key])) {
        if (oldest[key].length !== newest[key].length) {
          changedOrMissing.push(key)
        } else {
          let index = 0
          for (const oldValue of oldest[key]) {
            if (isPrimitive(oldValue) || isPojoDeep(oldValue)) {
              let equal
              try {
                equal = equalPrimitivesOrPojo(oldValue, newest[key][index], `${key}[${index}]`)
              } catch (ex) {
                equal = false
                // console.warn(`could not hash: ${key}[${index}] - assuming not equal`)
              }
              if (!equal) {
                changedOrMissing.push(key)
                break
              }
            } else if (oldValue !== oldest[key][index]) {
              changedOrMissing.push(key)
              break
            }
            index += 1
          }
        }
      } else {
        // property is a non-POJO object; cannot compare by hash so do nothing
        continue
      }
    }
  }

  // make sure no new keys are added
  for (const key of newKeys) {
    if (!oldKeys.includes(key)) {
      throw new Error(`Property present on second object but not on the first: "${key}"`)
    }
  }

  return changedOrMissing
}

export function logError(...args: any[]) {
  if (debug) {
    // eslint-disable-next-line
    console.error(...args)
  }
}

export function log(...args: any[]) {
  if (debug) {
    // eslint-disable-next-line
    console.log(...args)
  }
}

export function logObjects(...args: any) {
  for (const arg of args) {
    if (isPrimitive(arg)) {
      console.log(arg)
    } else if (typeof arg === 'object') {
      try {
        console.log(JSON.parse(JSON.stringify(arg)))
      } catch (ex) {
        console.log('could not stringify', Object.assign({}, ...arg))
      }
    }
  }
}
